import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Heart, BookOpen, Headphones, Mail, ArrowRight, Users, Globe, Star } from 'lucide-react';
import Icon from '../../components/AppIcon';


const About = () => {
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>About Fortified Mind | Scripture Memorization & Bible Study Ministry</title>
        <meta name="description" content="Fortified Mind is a nonprofit Christian ministry rooted in the Seventh-day Adventist tradition, open to all denominations. Discover our mission to help believers memorize Scripture, study the Bible, and grow closer to God." />
        <meta name="keywords" content="about Fortified Mind, Seventh-day Adventist ministry, SDA Bible tools, scripture memorization ministry, Bible study nonprofit, Christian ministry all denominations, Ellen White writings, EGW, closer walk with God" />
        <link rel="canonical" href="https://fortifiedmind.org/about" />
        <meta property="og:title" content="About Fortified Mind | Scripture Memorization & Bible Study Ministry" />
        <meta property="og:description" content="A nonprofit Christian ministry rooted in the SDA tradition, open to all believers. Free scripture memorization, Bible study tools, and devotional resources." />
        <meta property="og:url" content="https://fortifiedmind.org/about" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://fortifiedmind.org/assets/images/FortifiedMindSplash1-1769379659227.jpeg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About Fortified Mind | Scripture Memorization & Bible Study Ministry" />
        <meta name="twitter:description" content="A nonprofit Christian ministry rooted in the SDA tradition, open to all believers. Free scripture memorization, Bible study tools, and devotional resources." />
        <meta name="twitter:image" content="https://fortifiedmind.org/assets/images/FortifiedMindSplash1-1769379659227.jpeg" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "name": "About Fortified Mind",
          "url": "https://fortifiedmind.org/about",
          "description": "Fortified Mind is a nonprofit Christian ministry rooted in the Seventh-day Adventist tradition, open to all denominations. We provide free scripture memorization and Bible study tools.",
          "mainEntity": {
            "@type": "Organization",
            "name": "Fortified Mind",
            "url": "https://fortifiedmind.org",
            "description": "A nonprofit ministry dedicated to helping believers go deeper into God's Word through memorization, reflection, and study.",
            "foundingDate": "2024",
            "nonprofitStatus": "Nonprofit501c3",
            "knowsAbout": ["Scripture Memorization", "Bible Study", "Seventh-day Adventist", "Christian Ministry", "Spaced Repetition", "Ellen White Writings"]
          }
        })}</script>
      </Helmet>
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-primary/10 via-background to-accent/5 border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <div className="flex justify-center mb-6">
            <div className="bg-primary/10 p-4 rounded-full">
              <BookOpen className="w-12 h-12 text-primary" />
            </div>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-4">
            About Fortified Mind
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            A nonprofit ministry dedicated to helping believers go deeper into God's Word through memorization, reflection, and study.
          </p>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-14">

        {/* Mission Section */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-primary/10 p-2 rounded-lg">
              <Star className="w-5 h-5 text-primary" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">Our Mission</h2>
          </div>
          <div className="bg-card border border-border rounded-xl p-6 space-y-4 text-muted-foreground leading-relaxed">
            <p>
              Fortified Mind exists to foster a deeper, more personal connection with the Word of God. We believe that Scripture memorization, thoughtful reflection, and consistent study are among the most transformative spiritual disciplines a believer can cultivate.
            </p>
            <p>
              In a world full of noise and distraction, we want to help you hide God's Word in your heart — so that it shapes how you think, how you speak, and how you live. Whether you are just beginning your journey with Scripture or have been walking with God for decades, Fortified Mind is designed to meet you where you are.
            </p>
          </div>
        </section>

        {/* Our Tradition Section */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-accent/10 p-2 rounded-lg">
              <Globe className="w-5 h-5 text-accent" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">Our Tradition</h2>
          </div>
          <div className="bg-card border border-border rounded-xl p-6 space-y-4 text-muted-foreground leading-relaxed">
            <p>
              Fortified Mind is rooted in the Seventh-day Adventist (SDA) tradition — a faith community with a rich heritage of biblical scholarship, health, and holistic discipleship. Our tools and resources reflect that heritage, including access to Ellen G. White writings and SDA Bible commentaries.
            </p>
            <p>
              That said, <strong className="text-foreground">all are welcome here.</strong> Whether you are Baptist, Catholic, non-denominational, or simply someone who loves the Bible, Fortified Mind is a place for you. The Word of God belongs to everyone, and our doors are open to all who seek it.
            </p>
          </div>
        </section>

        {/* What We Offer */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-success/10 p-2 rounded-lg">
              <BookOpen className="w-5 h-5 text-success" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">What We Offer</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { icon: BookOpen, title: 'Scripture Memorization', desc: 'Spaced repetition tools to help you memorize and retain God\'s Word for life.' },
              { icon: Star, title: 'Bible Study Tools', desc: 'Commentaries, lexicons, interlinear texts, and cross-references for deeper study.' },
              { icon: Headphones, title: 'Audio Bible', desc: 'Listen to Scripture read aloud to reinforce learning and aid meditation.' },
              { icon: Users, title: 'Reading Plans', desc: 'Structured plans to guide you through the Bible systematically and consistently.' },
            ]?.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-card border border-border rounded-xl p-5 flex gap-4">
                <div className="bg-primary/10 p-2 rounded-lg h-fit">
                  <Icon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">{title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Coming Soon */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-warning/10 p-2 rounded-lg">
              <Headphones className="w-5 h-5 text-warning" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">What's Coming</h2>
          </div>
          <div className="bg-card border border-border rounded-xl p-6 space-y-4 text-muted-foreground leading-relaxed">
            <p>
              We are just getting started. Our vision for Fortified Mind extends well beyond what you see today. Here is what we are working toward:
            </p>
            <ul className="space-y-2 list-none">
              {[
                '🎙️ A podcast exploring Scripture, theology, and the Christian life',
                '📖 Expanded commentary and study resources',
                '🌍 Multi-language support to reach believers around the world',
              ]?.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p>
              We believe God has more in store for this ministry, and we are excited to grow alongside our community.
            </p>
          </div>
        </section>

        {/* Support / Donations */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-destructive/10 p-2 rounded-lg">
              <Heart className="w-5 h-5 text-destructive" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">Support This Ministry</h2>
          </div>
          <div className="bg-card border border-border rounded-xl p-6 space-y-4 text-muted-foreground leading-relaxed">
            <p>
              Fortified Mind is a nonprofit ministry. We do not charge for access to our tools, and we are committed to keeping the Word of God freely available to all. However, running and maintaining this platform does come with real costs — hosting, development, and ongoing improvements.
            </p>
            <p>
              If Fortified Mind has been a blessing to you, we would be grateful for your support. Your donations help us keep the lights on, improve the platform, and expand our reach to more believers around the world.
            </p>
            <div className="pt-2">
              <a
                href="/contact-us"
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 rounded-lg hover:bg-primary/90 transition-colors font-medium"
              >
                <Heart className="w-4 h-4" />
                Support Fortified Mind
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </section>

        {/* Prayer Request */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-primary/10 p-2 rounded-lg">
              <Users className="w-5 h-5 text-primary" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">Pray With Us</h2>
          </div>
          <div className="bg-card border border-border rounded-xl p-6 space-y-4 text-muted-foreground leading-relaxed">
            <p>
              We believe deeply in the power of prayer. As we build and grow this ministry, we covet the prayers of our community. Please pray for:
            </p>
            <ul className="space-y-2">
              {[
                'Wisdom and discernment as we develop new features and resources',
                'The users of this platform — that God\'s Word would take root in their hearts',
                'Provision to sustain and expand this ministry',
                'Unity and growth in the body of Christ across all traditions',
              ]?.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span className="text-primary mt-1">🙏</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p>
              If you have a prayer request or would like to share how Fortified Mind has impacted your walk with God, we would love to hear from you.
            </p>
            <div className="pt-2">
              <Link
                to="/contact-us"
                className="inline-flex items-center gap-2 border border-primary text-primary px-5 py-2.5 rounded-lg hover:bg-primary/10 transition-colors font-medium"
              >
                <Mail className="w-4 h-4" />
                Get in Touch
              </Link>
            </div>
          </div>
        </section>

        {/* Footer CTA */}
        <section className="text-center py-8 border-t border-border">
          <p className="text-muted-foreground mb-2">
            "Your word I have hidden in my heart, that I might not sin against You." — Psalm 119:11
          </p>
          <p className="text-sm text-muted-foreground">
            © {new Date()?.getFullYear()} Fortified Mind · A nonprofit ministry · All are welcome.
          </p>
        </section>

      </div>
    </div>
  );
};

export default About;
